import { SelectedInvoice } from '@/components/RelationActionMenu/Invoice/type';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { PdfViewer } from '@/components/pdfViewer';
import { useGetEDocPlan } from '@/context/services/e_doc_plan/EDocPlan.service';
import {
  EDocMetaDatum,
  ReceivedInvoiceReceiptType,
} from '@/context/services/invoice/type';
import { PdfHeader } from '@/features/invoices/components/Details/Components/Left/PdfHeader';
import { useGetScanDecision } from '@/features/invoices/components/Details/Components/hooks/useScanDecision';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useGetMfFileAndObjectURL } from '@service/mf_file/MfFile.service';
import classnames from 'classnames/bind';
import { FC, memo, useEffect } from 'react';
import styles from './Dialog.module.scss';

const cx = classnames.bind(styles);

export type Props = {
  invoiceId: string;
  mfFileId: string;
  fileName: string;
  searchId: string;
  setSelectedFile: (value: File | SelectedInvoice | null) => void;
  eDocMetaDatum?: EDocMetaDatum;
  showTimestamp: boolean;
  receiptType?: ReceivedInvoiceReceiptType;
};

export const MainPreview: FC<Props> = memo((props) => {
  return (
    <SuspenseErrorBoundary>
      <InnerMainPreview {...props} />
    </SuspenseErrorBoundary>
  );
});
MainPreview.displayName = 'MainPreview';

export const InnerMainPreview: FC<Props> = memo(
  ({
    invoiceId,
    mfFileId,
    fileName,
    searchId,
    setSelectedFile,
    eDocMetaDatum,
    showTimestamp,
    receiptType,
  }) => {
    const flagEDocMetadata = useGetFlags('E_DOC_METADATA');
    const { originalBlob, url } = useGetMfFileAndObjectURL(mfFileId);
    useEffect(() => {
      const file = new File([originalBlob], fileName ?? 'unknown', {
        lastModified: Date.now(),
        type: originalBlob.type,
      });
      setSelectedFile({
        file: file,
        mfFileId: mfFileId,
        invoiceId: invoiceId,
        searchId: searchId,
      });
    }, [
      fileName,
      invoiceId,
      mfFileId,
      searchId,
      originalBlob,
      setSelectedFile,
    ]);
    const { apReceiptTypeAlertType } = useGetEDocPlan();
    const scanDecision = useGetScanDecision(
      eDocMetaDatum,
      apReceiptTypeAlertType,
      true // Default to true since is_e_doc_ap_receipt_type_enabled is not available
    );
    return (
      <div className={cx(styles['previewContainer'])}>
        {flagEDocMetadata && (
          <PdfHeader
            timestamp={!!showTimestamp}
            scanDecision={scanDecision}
            isEmpty={!url}
            isEnabledEDoc={!!eDocMetaDatum}
            receiptType={receiptType ?? undefined}
          />
        )}
        <PdfViewer className={cx(styles['pdfViewerContainer'])} src={url} />
      </div>
    );
  }
);
InnerMainPreview.displayName = 'MainPreview';

export const EmptyPreview: FC = memo(() => {
  return <PdfViewer className={cx(styles['previewContainer'])} src='' />;
});
EmptyPreview.displayName = 'EmptyPreview';
